import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Input,
  Button,
  Dropdown,
  App,
  Empty,
} from "antd";
import {
  EllipsisOutlined,
  LoadingOutlined,
  MailOutlined,
  MessageOutlined,
  PhoneOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import theme from "../../themes";
import { filterControlCss } from "../../components/ContentLoading";
import {
  pageShellCss,
  pageTitleCss,
  searchFieldCss,
  tableScrollCss,
  wrapRowCss,
} from "../../styles/responsive";
import { ReactComponent as SearchIcon } from "../../assets/icons/search.svg";
import { ReactComponent as AddIcon } from "../../assets/icons/add.svg";
import { useAppDispatch, useAppSelector } from "../../store";
import {
  deleteAgentThunk,
  duplicateAgentThunk,
  getAgentsThunk,
} from "../../store/actions/agent.action";
import CreateAgentModal from "./CreateAgentModal";
import {
  AgentChannel,
  resolveAgentChannels,
} from "../../models/agent.model";
import { ROLES } from "../../models/user.model";
import dayjs from "dayjs";
import ToastMessage from "../../utils/toastMessage";
import type { SortOrder } from "antd/es/table/interface";
import { ReactComponent as ArrowUpIcon } from "../../assets/icons/arrowUp.svg";
import { ReactComponent as ArrowDownIcon } from "../../assets/icons/arrowDown.svg";
import { useTranslation } from "react-i18next";
import { getAgentDirectionLabel } from "../../utils/directionOptions";
import { useChannelSettings } from "../../hooks/useChannelSettings";

type ChannelFilter = "all" | AgentChannel;

const CHANNEL_FILTERS: ChannelFilter[] = [
  "all",
  "call",
  "sms",
  "whatsapp",
  "email",
];

const channelIcon = (key: string) => {
  if (key === "call") return <PhoneOutlined />;
  if (key === "sms") return <MessageOutlined />;
  if (key === "whatsapp") return <WhatsAppOutlined />;
  return <MailOutlined />;
};

const AgentsPage: React.FC = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { agents, loading } = useAppSelector((state) => state.agent);
  const { user } = useAppSelector((state) => state.auth);
  const { isEnabled, flags } = useChannelSettings();
  const visibleFilters = CHANNEL_FILTERS.filter(
    (filter) => filter === "all" || isEnabled(filter)
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [sorter, setSorter] = useState<{
    field: "createdAt" | "agent_name";
    order: SortOrder | undefined;
  }>({ field: "createdAt", order: "descend" });
  const { message, modal } = App.useApp();
  const toast = ToastMessage(message);

  const channelLabel = (key: ChannelFilter) => {
    if (key === "all") return t("agents.v2.all");
    if (key === "call") return t("agents.v2.call");
    if (key === "sms") return t("agents.v2.text");
    if (key === "whatsapp") return t("agents.v2.whatsapp");
    return t("agents.v2.email");
  };

  useEffect(() => {
    if (channelFilter !== "all" && !isEnabled(channelFilter)) {
      setChannelFilter("all");
    }
  }, [channelFilter, flags, isEnabled]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const fetchAgents = async (
    page = currentPage,
    keyword = debouncedSearch,
    sortField: "createdAt" | "agent_name" = sorter.field,
    sortOrder?: SortOrder,
    channel = channelFilter
  ) => {
    try {
      const sortBy: Record<string, 1 | -1> = {};
      if (sortField === "createdAt" || sortField === "agent_name") {
        if (sortOrder) {
          sortBy[sortField] = sortOrder === "ascend" ? 1 : -1;
        }
      }
      await dispatch(
        getAgentsThunk({
          limit: pageSize,
          page,
          keyword,
          channel: channel === "all" ? undefined : channel,
          sort: Object.keys(sortBy).length ? sortBy : undefined,
        })
      ).unwrap();
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleCreateAgentSuccess = () => {
    try {
      fetchAgents(1, debouncedSearch);
    } catch (error) {
      toast.error(t("agents.failedToCreateAgent"), 5);
    } finally {
      setIsModalOpen(false);
    }
  };

  const handleCustomSort = (field: "createdAt" | "agent_name") => {
    let nextOrder: SortOrder = "descend";
    if (sorter.field === field) {
      nextOrder = sorter.order === "ascend" ? "descend" : "ascend";
    }
    setSorter({ field, order: nextOrder });
    fetchAgents(currentPage, debouncedSearch, field, nextOrder);
  };

  const handleDuplicateAgent = async (id: string) => {
    if (duplicatingId) return;
    setDuplicatingId(id);
    try {
      await dispatch(duplicateAgentThunk({ id })).unwrap();
      toast.success(t("agents.agentDuplicatedSuccessfully"), 5);
      setCurrentPage(1);
      await fetchAgents(1, debouncedSearch);
    } catch (error) {
      toast.error(
        typeof error === "string" && error
          ? error
          : t("agents.failedToDuplicateAgent"),
        5
      );
    } finally {
      setDuplicatingId(null);
    }
  };

  const handleDeleteAgent = (id: string, name: string) => {
    modal.confirm({
      title: t("agents.v2.deleteTitle"),
      content: t("agents.v2.deleteConfirm", { name }),
      okText: t("common.delete"),
      okButtonProps: { danger: true },
      cancelText: t("common.cancel"),
      onOk: async () => {
        try {
          await dispatch(deleteAgentThunk({ id })).unwrap();
          toast.success(t("agents.agentDeletedSuccessfully"), 5);
          fetchAgents(currentPage, debouncedSearch);
        } catch (error) {
          toast.error(
            typeof error === "string" && error
              ? error
              : t("agents.failedToDeleteAgent"),
            5
          );
        }
      },
    });
  };

  const columns = [
    {
      title: (
        <span
          style={{
            cursor: "pointer",
            color: sorter.field === "agent_name" ? "#000" : "inherit",
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleCustomSort("agent_name");
          }}
        >
          {t("agents.agentName")}{" "}
          {sorter.field === "agent_name" &&
            (sorter.order === "ascend" ? (
              <ArrowUpIcon style={{ marginLeft: 4, verticalAlign: "middle" }} />
            ) : (
              <ArrowDownIcon
                style={{ marginLeft: 4, verticalAlign: "middle" }}
              />
            ))}
        </span>
      ),
      dataIndex: "agent_name",
      key: "agent_name",
      sorter: false,
      sortOrder: undefined,
      render: (text: string, record: any) => (
        <AgentNameCell>
          <a
            href={`/agents/${record.key}`}
            style={{ color: "#1890ff", fontWeight: 500 }}
            onClick={(e) => {
              e.preventDefault();
              navigate(`/agents/${record.key}`);
            }}
          >
            {text}
          </a>
          {record.description ? (
            <AgentDescription>{record.description}</AgentDescription>
          ) : null}
        </AgentNameCell>
      ),
      onHeaderCell: () => ({ style: { cursor: "pointer" } }),
    },
    {
      title: t("agents.v2.channel"),
      dataIndex: "channels",
      key: "channels",
      render: (channels: AgentChannel[]) => (
        <ChannelIcons>
          {channels.map((channel) => (
            <span key={channel} title={channelLabel(channel)}>
              {channelIcon(channel)}
            </span>
          ))}
        </ChannelIcons>
      ),
    },
    {
      title: t("agents.v2.direction"),
      dataIndex: "direction",
      key: "direction",
      render: (direction: "inbound" | "outbound", record: any) => (
        <DirectionPill>
          {getAgentDirectionLabel(direction, record.channels, t)}
        </DirectionPill>
      ),
    },
    {
      title: (
        <span
          style={{
            cursor: "pointer",
            color: sorter.field === "createdAt" ? "#000" : "inherit",
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleCustomSort("createdAt");
          }}
        >
          {t("agents.createdTime")}{" "}
          {sorter.field === "createdAt" &&
            (sorter.order === "ascend" ? (
              <ArrowUpIcon style={{ marginLeft: 4, verticalAlign: "middle" }} />
            ) : (
              <ArrowDownIcon
                style={{ marginLeft: 4, verticalAlign: "middle" }}
              />
            ))}
        </span>
      ),
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: false,
      sortOrder: undefined,
      onHeaderCell: () => ({ style: { cursor: "pointer" } }),
      render: (value: string) => <CreatedTime>{value || "—"}</CreatedTime>,
    },
    {
      title: "",
      key: "action",
      render: (_: any, record: any) => {
        const menuItems = [
          {
            key: "edit",
            label: t("common.edit"),
            onClick: () => navigate(`/agents/${record.key}`),
          },
          ...(user?.role === ROLES.ADMIN
            ? [
                {
                  key: "duplicate",
                  label: t("common.duplicate"),
                  disabled: Boolean(duplicatingId),
                  onClick: () => handleDuplicateAgent(record.key),
                },
                {
                  key: "delete",
                  label: t("common.delete"),
                  danger: true,
                  onClick: () => handleDeleteAgent(record.key, record.agent_name),
                },
              ]
            : []),
        ];

        return (
          <Dropdown
            menu={{
              items: menuItems,
            }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button type="text" icon={<EllipsisOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, channelFilter]);

  useEffect(() => {
    fetchAgents(currentPage, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch, channelFilter]);

  const counts = agents.channel_counts || {};

  return (
    <Page>
      <HeaderRow>
        <Title>{t("agents.title")}</Title>
        {user?.role === ROLES.ADMIN && (
          <CreateButton onClick={() => setIsModalOpen(true)}>
            <AddIcon /> {t("agents.createAgent")}
          </CreateButton>
        )}
      </HeaderRow>
      <CardWrapper>
        <Toolbar>
          <FilterRow>
            {visibleFilters.map((filter) => (
              <FilterPill
                key={filter}
                type="button"
                $active={channelFilter === filter}
                onClick={() => setChannelFilter(filter)}
              >
                {loading && channelFilter === filter ? <LoadingOutlined /> : null}
                {channelLabel(filter)}
                <FilterCount $active={channelFilter === filter}>
                  {filter === "all" ? counts.all ?? agents.pagination.totalItems ?? 0 : counts[filter] ?? 0}
                </FilterCount>
              </FilterPill>
            ))}
          </FilterRow>
          <SearchWrapper>
            <SearchIcon />
            <CustomSearch
              placeholder={t("agents.searchAgent")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchWrapper>
        </Toolbar>
        <StyledTable
          dataSource={agents.agents.map((agent) => ({
            key: agent._id,
            agent_name: agent.agent_name,
            description: agent.agent_description,
            channels: resolveAgentChannels(agent.channels),
            direction: agent.direction,
            createdAt: dayjs(agent.createdAt).format("h:mm A, MMM D, YYYY"),
          }))}
          loading={loading}
          columns={columns}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: agents.pagination.totalItems,
            position: ["bottomCenter"],
            onChange: (page) => setCurrentPage(page),
            showSizeChanger: false,
          }}
          rowKey="key"
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <EmptyCopy>
                    <EmptyStateTitle>{t("common.noData")}</EmptyStateTitle>
                    <EmptyStateHint>{t("agents.v2.emptyChannel")}</EmptyStateHint>
                  </EmptyCopy>
                }
              />
            ),
          }}
        />
      </CardWrapper>
      <CreateAgentModal
        visible={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onCreateSuccess={handleCreateAgentSuccess}
      />
    </Page>
  );
};

export default AgentsPage;

const Page = styled.div`
  background: ${theme.colors.grey3};
  display: flex;
  flex-direction: column;
  gap: 32px;
  ${pageShellCss}
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  ${pageTitleCss}
`;

const CreateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${theme.colors.green1};
  color: #fff;
  border: none;
  border-radius: 8px;
  height: 40px;
  padding: 0 16px;
  font-size: 16px;
  font-weight: 400;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #047857;
  }
  ${filterControlCss}
`;

const CardWrapper = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${theme.colors.grey2};
  ${tableScrollCss}
`;

const HeaderRow = styled.div`
  ${wrapRowCss}
`;

const Toolbar = styled.div`
  margin-bottom: 16px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  flex-wrap: wrap;
  min-width: 0;
`;

const FilterPill = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 99px;
  cursor: pointer;
  white-space: nowrap;
  font-size: 14px;
  line-height: 20px;
  color: ${({ $active }) => ($active ? theme.colors.green1 : "#000")};
  background: ${({ $active }) => ($active ? theme.colors.green2 : "#fff")};
  border: 1px solid
    ${({ $active }) => ($active ? theme.colors.green1 : theme.colors.grey2)};
  ${filterControlCss}
`;

const FilterCount = styled.span<{ $active: boolean }>`
  font-size: 14px;
  line-height: 20px;
  color: ${({ $active }) => ($active ? theme.colors.green1 : theme.colors.grey1)};
`;

const SearchWrapper = styled.div`
  ${searchFieldCss}
  display: flex;
  background-color: ${theme.colors.grey4};
  border: 1px solid ${theme.colors.grey2};
  border-radius: 8px;
  padding: 0 8px;
  align-items: center;
  box-sizing: border-box;
  gap: 8px;
  flex: none;
`;

const CustomSearch = styled(Input)`
  border-radius: 12px;
  height: 40px;
  font-size: 16px;
  border: none;
  box-shadow: none;
  background-color: ${theme.colors.grey4};
  padding: 0;

  &:hover,
  &:focus {
    border: none;
    box-shadow: none;
    background-color: ${theme.colors.grey4};
  }
`;

const EmptyCopy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const EmptyStateTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const EmptyStateHint = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: ${theme.colors.grey1};
`;

const AgentNameCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AgentDescription = styled.span`
  font-size: 14px;
  line-height: 20px;
  color: ${theme.colors.grey1};
  font-weight: 400;
`;

const ChannelIcons = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  color: #1f1f1f;
`;

const DirectionPill = styled.span`
  padding: 5px 12px;
  border-radius: 12px;
  border: 1px solid ${theme.colors.grey2};
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: #000;
  white-space: nowrap;
`;

const CreatedTime = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #000;
  white-space: nowrap;
`;

const StyledTable = styled(Table)`
  .ant-table-thead > tr > th {
    background-color: white;
    border-bottom: none;
    padding: 10px 16px;
    font-size: 14px;
    line-height: 20px;
    color: ${theme.colors.grey1};
    font-weight: 500;
  }

  .ant-table-thead > tr > th::before {
    display: none;
  }

  .ant-table-tbody > tr > td {
    padding: 16px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    border-bottom: none;
    height: 64px;
  }

  .ant-table-tbody > tr:nth-child(odd) {
    background-color: ${theme.colors.grey3};
  }

  .ant-table-tbody > tr:nth-child(even) {
    background-color: ${theme.colors.grey4};
  }

  .ant-table table {
    border-spacing: 0 4px;
    border-collapse: separate;
  }

  .ant-table-tbody > tr > td:first-child {
    border-top-left-radius: 16px;
    border-bottom-left-radius: 16px;
  }

  .ant-table-tbody > tr > td:last-child {
    border-top-right-radius: 16px;
    border-bottom-right-radius: 16px;
  }

  .ant-table-column-sorters .ant-table-column-sorter {
    display: none !important;
  }
`;
